import { z } from 'zod';
import {
  IDEMPOTENCY_HEADER,
  batchMovementSchema,
  paginatedSchema,
  receiptWithLinesSchema,
  stockGroupSchema,
  stockPlaceSchema,
  type BatchMovementDto,
  type MoveStockInput,
  type Paginated,
  type ReceiptLineInput,
  type ReceiptWithLinesDto,
  type StockGroupDto,
  type StockPlaceDto,
} from '@eombor/contracts';
import { apiClient } from '../../auth/model/auth.store.js';
import type { ApiClient } from '../../../shared/api/client.js';

/**
 * Qoldiq endpointlari. Manba:
 *   `stock/infrastructure/http/{receipt,stock}.controller.ts`
 *   `stock/domain/receipt.ts` (`ReceiptWithLines`, `StockGroup`, `StockPlace`)
 *
 * Javob sxemalari `@eombor/contracts` dan olinadi. Mavjud qoldiq
 * (`availableQty`) SERVERDA hisoblanadi va bu yerda QAYTA hisoblanmaydi:
 * `qty - reservedQty` formulasi bitta joyda (`domain/quantity.ts`) turishi
 * kerak, aks holda ikki raqam ajralib ketadi.
 */

const stockGroupPageSchema = paginatedSchema(stockGroupSchema);
const stockPlaceListSchema = z.array(stockPlaceSchema);
const batchMovementListSchema = z.array(batchMovementSchema);

/**
 * Kirim hujjatining SIM ustidagi shakli.
 *
 * `createReceiptSchema` da `receivedAt: Date` (`z.coerce.date()` chiqishi),
 * JSON da esa u baribir ISO satr bo'lib ketadi. Shu sababli mijoz turi
 * satrni ochiq aytadi — `Date` ni `JSON.stringify` ga tashlab, tur bilan
 * haqiqat ajralib qolmaydi.
 */
export interface CreateReceiptBody {
  warehouseId: string;
  mtuAktNo?: string;
  supplier?: string;
  /** ISO sana yoki sana-vaqt: `2026-08-17` ham, to'liq ISO ham qabul qilinadi. */
  receivedAt: string;
  note?: string;
  lines: ReceiptLineInput[];
}

export interface StockSearchQuery {
  q?: string;
  mtuId?: string;
  warehouseId?: string;
  /** Aniq nomenklatura — "shu mahsulot qaysi partiyada, qaysi katakda". */
  nomenclatureId?: string;
  /** Sxemada `'true' | 'false'` SATR — bu yerda ham shunday yuboriladi. */
  onlyAvailable?: boolean;
  page?: number;
  limit?: number;
}

export interface StockApi {
  /**
   * Kirim hujjati. `Idempotency-Key` MAJBURIY argument: tarmoq uzilib qayta
   * yuborilganda hujjat ikkilanmasligi chaqiruvchining xohishiga qoldirilmaydi.
   */
  createReceipt: (body: CreateReceiptBody, idempotencyKey: string) => Promise<ReceiptWithLinesDto>;
  /** Xato kirimni bekor qiladi. `DELETE` emas: hujjat qoladi, teskari yozuv qo'shiladi. */
  cancelReceipt: (id: string, reason: string) => Promise<ReceiptWithLinesDto>;
  search: (query?: StockSearchQuery) => Promise<Paginated<StockGroupDto>>;
  byCell: (cellId: string) => Promise<StockPlaceDto[]>;
  /** Partiya harakat tarixi — eng yangisi birinchi (server tartiblaydi). */
  movements: (batchId: string) => Promise<BatchMovementDto[]>;
  /**
   * Mahsulotni boshqa katakka ko'chiradi. `reason` MAJBURIY — tarixda
   * "nega ko'chdi" savoli javobsiz qolmasligi kerak.
   */
  move: (body: MoveStockInput) => Promise<StockPlaceDto[]>;
}

const searchQueryString = (query: StockSearchQuery): string => {
  const params = new URLSearchParams({
    page: String(query.page ?? 1),
    limit: String(query.limit ?? 20),
  });
  if (query.q !== undefined && query.q !== '') params.set('q', query.q);
  if (query.mtuId !== undefined && query.mtuId !== '') params.set('mtuId', query.mtuId);
  if (query.warehouseId !== undefined && query.warehouseId !== '') {
    params.set('warehouseId', query.warehouseId);
  }
  if (query.nomenclatureId !== undefined && query.nomenclatureId !== '') {
    params.set('nomenclatureId', query.nomenclatureId);
  }
  if (query.onlyAvailable !== undefined) {
    params.set('onlyAvailable', query.onlyAvailable ? 'true' : 'false');
  }
  return params.toString();
};

export const createStockApi = (client: ApiClient): StockApi => ({
  createReceipt: (body, idempotencyKey) =>
    client.apiFetch('/receipts', {
      method: 'POST',
      headers: { [IDEMPOTENCY_HEADER]: idempotencyKey },
      body: JSON.stringify(body),
      schema: receiptWithLinesSchema,
    }),

  cancelReceipt: (id, reason) =>
    client.apiFetch(`/receipts/${encodeURIComponent(id)}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
      schema: receiptWithLinesSchema,
    }),

  search: (query = {}) =>
    client.apiFetch(`/stock/search?${searchQueryString(query)}`, { schema: stockGroupPageSchema }),

  byCell: (cellId) =>
    client.apiFetch(`/stock/by-cell/${encodeURIComponent(cellId)}`, {
      schema: stockPlaceListSchema,
    }),

  move: (body) =>
    client.apiFetch('/stock/move', {
      method: 'POST',
      body: JSON.stringify(body),
      schema: stockPlaceListSchema,
    }),

  movements: (batchId) =>
    client.apiFetch(`/stock/batches/${encodeURIComponent(batchId)}/movements`, {
      schema: batchMovementListSchema,
    }),
});

export const stockApi = createStockApi(apiClient);
